import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/headers/AdminHeader';
import iconTitle from '../../assets/img/icon-square.svg';
import iconPdf from '../../assets/img/icon-pdf.svg';
import InsuranceApplicationListPop from '../../components/popups/InsuranceApplicationListPop';
import InsuranceApplicationPDFPop from '../../components/popups/InsuranceApplicationPDFPop';
import '../../styles/InsuranceApplicationList.css';
import '../../styles/button.css';
import '../../styles/PopUp.css';
import '../../styles/InsuranceApplicationDetail.css';
import { fetchInsuranceApplications, reviewInsuranceApplication } from '../../services/api/InsuranceService';
import { INSURANCE_TYPE, getInsuranceTypeShortName, getInsuranceTypeName } from '../../utils/insuranceTypeUtils';

const InsuranceApplicationList = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('未処理');
  const [agencyFilter, setAgencyFilter] = useState('すべて');
  const [typeFilter, setTypeFilter] = useState(INSURANCE_TYPE.ALL);
  const [agencies, setAgencies] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  // グループ化された申請データを保持する状態
  const [groupedApplications, setGroupedApplications] = useState({});
  // 展開されたグループのIDを管理
  const [expandedGroups, setExpandedGroups] = useState({});
  // ポップアップの状態を管理
  const [showPopup, setShowPopup] = useState(false);
  const [popupFiles, setPopupFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  // コメントを管理するステート
  const [comments, setComments] = useState({});
  // 却下状態を表示するアプリケーションID
  const [showCommentFor, setShowCommentFor] = useState({});
  // 確認ポップアップ用の状態を追加
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [confirmationPopupData, setConfirmationPopupData] = useState(null);
  // API loading state
  const [loading, setLoading] = useState(true);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  // 初回読み込みフラグ（二重呼び出し防止用）
  const initialFetchDone = useRef(false);

  // PDFポップアップを表示する関数
  const openPdfPopup = (files, startIndex = 0) => {
    if (!files || files.length === 0) {
      console.error('ファイルが見つかりません');
      return;
    }

    console.log('PDFポップアップを開きます。ファイル:', files);

    // ファイル配列をセット
    setPopupFiles(files);

    // 現在のファイルインデックスを設定
    setCurrentFileIndex(startIndex);

    // ポップアップを表示
    setShowPopup(true);
  };

  // ポップアップを閉じる関数
  const closePopup = () => {
    setShowPopup(false);
  };

  // 確認ポップアップを閉じる関数
  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
  };

  // 確認ポップアップ
  const finalizeStatusChange = async () => {
    // Close the confirmation popup first
    closeConfirmationPopup();

    if (!confirmationPopupData) return;

    try {
      // Convert status to API format
      const apiStatus = confirmationPopupData.status === '却下' ? 'rejected' : 'approved';

      // Get any comments if status is rejected
      const comment = confirmationPopupData.status === '却下'
        ? comments[confirmationPopupData.id] || ''
        : '';

      // Show loading state if needed
      setLoading(true);

      // Make the API call
      const response = await reviewInsuranceApplication(
        confirmationPopupData.id,
        apiStatus,
        comment
      );

      // Handle successful response
      console.log("API response:", response);

      // Fetch updated data after successful API call
      await processApplicationsData();

    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setLoading(false);
    }
  };

  // 次のファイルに移動
  const nextFile = () => {
    setCurrentFileIndex((prev) => (prev < popupFiles.length - 1 ? prev + 1 : prev));
  };

  // 前のファイルに移動
  const prevFile = () => {
    setCurrentFileIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  // グループの展開/折りたたみを切り替える
  const toggleGroup = (agencyId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [agencyId]: !prev[agencyId]
    }));
  };

  // APIから取得した保険申請データを処理し、関連するすべての状態を更新する
  const processApplicationsData = async () => {
    const response = await fetchInsuranceApplications();

    // APIからのレスポンスを適切な形式に変換
    const formattedData = response.map(app => {
      // ドキュメントの配列から{filename, id}の配列を作成
      const files = app.documents.map(doc => ({
        filename: doc?.filename || 'ファイル名なし',
        id: doc?.id || null
      }));

      // APIのステータスをフロントエンドの表示用に変換
      const statusMap = {
        'pending': '未処理',
        'rejected': '却下',
        'approved': '承認'
      };

      // 日付のフォーマット
      const date = new Date(app.application_date);
      const formattedDate = `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;

      return {
        id: app.id,
        date: formattedDate,
        agency: app.agency_name,
        company: app.insurance_company_name,
        type: app.insurance_company_type,
        files: files, // filenameとidを含むオブジェクト配列
        status: statusMap[app.status] || app.status,
        // ステータスが未処理の場合は編集可能
        editable: app.status === 'pending',
        // ドキュメントの元データも保持（必要に応じて）
        rawDocuments: app.documents
      };
    });
    console.log("formattedData:", formattedData);
    setApplications(formattedData);

    // 代理店リストを抽出して重複を排除
    const uniqueAgencies = [...new Set(formattedData.map(app => app.agency))];
    // IDと名前を含むオブジェクトの配列を作成
    const agencyObjects = uniqueAgencies.map(name => {
      const agency = formattedData.find(app => app.agency === name);
      return {
        id: agency?.agency_id,
        name: name
      };
    });
    setAgencies(agencyObjects);

    // 申請を代理店名でグループ化
    const grouped = formattedData.reduce((acc, app) => {
      if (!acc[app.agency]) {
        acc[app.agency] = {
          count: 0,
          items: []
        };
      }
      acc[app.agency].count += 1;
      acc[app.agency].items.push(app);
      return acc;
    }, {});

    setGroupedApplications(grouped);

    // 既存の展開状態を保持しつつ、新しい代理店を追加
    setExpandedGroups(prev => {
      const newExpanded = { ...prev };
      uniqueAgencies.forEach(agency => {
        if (newExpanded[agency] === undefined) {
          newExpanded[agency] = false; // デフォルトは折りたたみ
        }
      });
      return newExpanded;
    });

    // 初期コメントを設定
    const initialComments = {};
    const initialShowComment = {};
    formattedData.forEach(app => {
      if (app.status === '却下') {
        initialComments[app.id] = '';
        initialShowComment[app.id] = app.editable; // 編集可能なもののみ表示
      }
    });
    setComments(initialComments);
    setShowCommentFor(initialShowComment);
  }

  useEffect(() => {
    // 開発環境での二重呼び出しを防止
    if (process.env.NODE_ENV === 'development' && !initialFetchDone.current) {
      initialFetchDone.current = true;
      return;
    }

    // コンポーネントがマウントされているかどうかを追跡するフラグ
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) setLoading(true);

      try {
        // コンポーネントがまだマウントされている場合のみ状態を更新
        if (isMounted) {
          await processApplicationsData();
        }
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // クリーンアップ関数でマウント状態フラグを更新
    return () => {
      isMounted = false;
    };
  }, [currentPage, statusFilter, agencyFilter, itemsPerPage, typeFilter]); // 依存配列に必要な値を追加

  const handleTypeFilterChange = (type) => {
    setTypeFilter(type);
    setCurrentPage(1); // フィルター変更時は1ページ目に戻る

    if (type === INSURANCE_TYPE.ALL) return;

    const agenciesWithType = applications.filter(app => app.type === type).map(app => app.agency);
    const uniqueAgencies = [...new Set(agenciesWithType)];
    setExpandedGroups(prev => {
      const newExpanded = { ...prev };
      uniqueAgencies.forEach(agency => {
        newExpanded[agency] = true; // 該当する代理店のグループを展開
      });
      return newExpanded;
    });

  };

  // 状態フィルターの変更ハンドラ
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // フィルター変更時は1ページ目に戻る

    // 状態が選択されたとき、その状態を持つ項目を含むグループを展開する
    if (status !== 'すべて') {
      // 「未処理」の場合はeditableフラグでフィルタリング
      if (status === '未処理') {
        const agenciesWithEditable = applications
          .filter(app => app.editable)
          .map(app => app.agency);

        const uniqueAgencies = [...new Set(agenciesWithEditable)];

        setExpandedGroups(prev => {
          const newExpanded = { ...prev };
          uniqueAgencies.forEach(agency => {
            newExpanded[agency] = true; // 該当する代理店のグループを展開
          });
          return newExpanded;
        });
      } else {
        // その他の状態の場合は通常のステータスでフィルタリング
        const agenciesWithStatus = applications
          .filter(app => app.status === status)
          .map(app => app.agency);

        const uniqueAgencies = [...new Set(agenciesWithStatus)];

        setExpandedGroups(prev => {
          const newExpanded = { ...prev };
          uniqueAgencies.forEach(agency => {
            newExpanded[agency] = true; // 該当する代理店のグループを展開
          });
          return newExpanded;
        });
      }
    }
  };

  // 状態変更ハンドラ
  const handleStatusChange = (appId, newStatus) => {
    // Only update the local state
    setApplications(prev =>
      prev.map(app =>
        app.id === appId ? { ...app, status: newStatus } : app
      )
    );

    // Set the selected application
    setSelectedApplication(appId);

    // "却下" (Rejected) status requires comments
    if (newStatus === '却下') {
      setComments(prev => ({
        ...prev,
        [appId]: prev[appId] || ''
      }));
      setShowCommentFor(prev => ({
        ...prev,
        [appId]: true
      }));
    } else {
      // Hide comment field for other statuses
      setShowCommentFor(prev => {
        const newShow = { ...prev };
        delete newShow[appId];
        return newShow;
      });
    }
  };

  // コメント変更ハンドラ
  const handleCommentChange = (appId, comment) => {
    setComments(prev => ({
      ...prev,
      [appId]: comment
    }));
  };

  // 代理店フィルターの変更ハンドラ
  const handleAgencyFilterChange = (e) => {
    setAgencyFilter(e.target.value);
    setCurrentPage(1); // フィルター変更時は1ページ目に戻る
  };

  // ラジオボタン選択ハンドラ
  const handleSelectApplication = (appId) => {
    setSelectedApplication(appId);
  };


  const handleConfirm = () => {
    if (selectedApplication) {
      // 選択された申請データを取得
      const selectedAppData = applications.find(app => app.id === selectedApplication);
      if (selectedAppData) {
        console.log("Selected application data:", selectedAppData);
        console.log("Status:", selectedAppData.status);

        // 確認ポップアップのデータを設定
        setConfirmationPopupData(selectedAppData);
        setShowConfirmationPopup(true);
      }
    } else {
      alert('申請を選択してください');
    }
  };

  // 戻るボタンハンドラ
  const handleReturn = () => {
    navigate(-1);
  };

  // ページネーションハンドラ
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // ファイル表示セルのレンダリング
  const renderFileCell = (files) => {
    if (!files || files.length === 0) {
      return <span>ファイルなし</span>;
    }

    if (files.length > 5) {
      return (
        <button className="view-files-btn" onClick={() => openPdfPopup(files)}>
          PDFファイルを表示（{files.length}件）
        </button>
      );
    } else {
      return (
        <div className="individual-files">
          {files.map((file, index) => (
            <div
              key={index}
              className="file-link"
              onClick={() => openPdfPopup(files, index)}
            >
              {/* オブジェクト全体ではなく、確実に文字列を表示 */}
              <span>{file?.filename || 'ファイル名なし'}</span>
              <img src={iconPdf} alt="PDF" className="pdf-icon" />
            </div>
          ))}
        </div>
      );
    }
  };

  // フィルタリングロジック
  const filteredApplications = applications.filter(app => {
    // 状態フィルター
    // 「未処理」の場合はeditableフラグでフィルタリング
    if (statusFilter === '未処理' && !app.editable) {
      return false;
    }

    // 「却下」か「承認」の場合、編集可能な項目を除外
    if ((statusFilter === '却下' || statusFilter === '承認') && app.editable) {
      return false;
    }

    // それ以外の状態は通常のフィルタリング
    const statusMatches = statusFilter === 'すべて' || app.status === statusFilter;

    // 代理店フィルター
    const agencyMatches = agencyFilter === 'すべて' || app.agency === agencyFilter;

    // 保険タイプフィルター
    const typeMatches = typeFilter === INSURANCE_TYPE.ALL || app.type === typeFilter;

    return statusMatches && agencyMatches && typeMatches;
  });

  // フィルタリングされたグループを生成
  const filteredGroups = {};
  filteredApplications.forEach(app => {
    if (!filteredGroups[app.agency]) {
      filteredGroups[app.agency] = {
        count: 0,
        items: []
      };
    }
    filteredGroups[app.agency].count += 1;
    filteredGroups[app.agency].items.push(app);
  });

  // グループごとのレンダリング
  const renderGroupedApplications = () => {
    return Object.entries(filteredGroups).map(([agency, data]) => {
      const isExpanded = expandedGroups[agency];
      const hasMultipleItems = data.items.length > 1;

      // 状態ごとの数を集計
      const statusCounts = data.items.reduce((acc, item) => {
        // 編集可能な項目は「未処理」としてカウント
        if (item.editable) {
          acc['未処理'] = (acc['未処理'] || 0) + 1;
        } else {
          // 編集不可の項目は実際のステータスでカウント
          const status = item.status;
          acc[status] = (acc[status] || 0) + 1;
        }
        return acc;
      }, {});

      // 1件のみの場合は通常の行として表示
      if (!hasMultipleItems) {
        const item = data.items[0];
        return (
          <React.Fragment key={item.id}>
            <tr>
              <td>
                {/* 編集可能な場合のみラジオボタンを表示 */}
                {item.editable ? (
                  <label className="c-radio-btn">
                    <input
                      type="radio"
                      name="selected-app"
                      className="c-radio-btn__inner"
                      checked={selectedApplication === item.id}
                      onChange={() => handleSelectApplication(item.id)}
                    />
                  </label>
                ) : (
                  <span>&nbsp;</span> // 編集不可の場合は空のスペース
                )}
              </td>
              <td>{item.date}</td>
              <td>{item.agency}</td>
              <td>{getInsuranceTypeName(item.type)}</td>
              <td>{item.company}</td>
              <td className="files-cell">
                {renderFileCell(item.files)}
              </td>
              <td>
                {item.editable ? (
                  <div className="select-box c-max-x-small">
                    <select
                      className="status-select c-max-x-small"
                      value={item.status}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    >
                      <option value="承認">承認</option>
                      <option value="却下">却下</option>
                    </select>
                  </div>
                ) : (
                  <span className={`status-tag status-${item.status}`}>{item.status}</span>
                )}
              </td>
            </tr>
            {/* コメント欄を表示（却下かつ編集可能な場合のみ） */}
            {showCommentFor[item.id] && (
              <tr className="comment-row">
                <td></td>
                <td className='space-one-column' colSpan={5}>
                  <div className="comment-label">コメント</div>
                  <div className="comment-box">
                    <textarea
                      value={comments[item.id] || ''}
                      onChange={(e) => handleCommentChange(item.id, e.target.value)}
                      className="comment-input"
                    />
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      }

      // 複数件ある場合はアコーディオン表示
      return (
        <React.Fragment key={agency}>
          <tr className="group-header">
            <td>&nbsp;
            </td>
            <td>
              <span className="agency-date-range">
                {(() => {
                  // 日付の最小値と最大値を取得
                  const dates = data.items.map(item => new Date(item.date));
                  const minDate = new Date(Math.min(...dates));
                  const maxDate = new Date(Math.max(...dates));

                  // 日付のフォーマット
                  const formatDate = (date) => {
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const day = date.getDate().toString().padStart(2, '0');
                    return `${year}/${month}/${day}`;
                  };

                  // 同じ日付の場合は1つだけ表示
                  if (minDate.getTime() === maxDate.getTime()) {
                    return formatDate(minDate);
                  }

                  // 期間を表示
                  return `${formatDate(minDate)} 〜 ${formatDate(maxDate)}`;
                })()}
              </span>
            </td>
            <td colSpan={5}>
              <button
                className={`accordion-btn ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleGroup(agency)}
              >
                <div className="agency-group-info">
                  <div className="agency-group-name">{agency}<span className="agency-group-count">（{data.count}件）</span></div>

                  <div className="agency-status-summary">
                    {/* 未処理と編集可能をまとめて表示 */}
                    {statusCounts['未処理'] > 0 && (
                      <span className="status-count 未処理 status-未処理">未処理{statusCounts['未処理']}件</span>
                    )}
                    {statusCounts['却下'] > 0 && (
                      <span className="status-count 却下 status-却下">却下{statusCounts['却下']}件</span>
                    )}
                    {statusCounts['承認'] > 0 && (
                      <span className="status-count 承認 status-承認">承認{statusCounts['承認']}件</span>
                    )}
                  </div>
                </div>
                <div className={`c-plus-btn ${isExpanded ? 'rotated' : ''}`}></div>
              </button>
            </td>
          </tr>
          {isExpanded && data.items.map(item => (
            <React.Fragment key={item.id}>
              <tr className="group-item">
                <td>
                  {/* 編集可能な場合のみラジオボタンを表示 */}
                  {item.editable ? (
                    <label className="c-radio-btn">
                      <input
                        type="radio"
                        name="selected-app"
                        className="c-radio-btn__inner"
                        checked={selectedApplication === item.id}
                        onChange={() => handleSelectApplication(item.id)}
                      />
                    </label>
                  ) : (
                    <span>&nbsp;</span> // 編集不可の場合は空のスペース
                  )}
                </td>
                <td>{item.date}</td>
                <td>{item.agency}</td>
                <td>{getInsuranceTypeName(item.type)}</td>
                <td>{item.company}</td>
                <td className="files-cell">
                  {renderFileCell(item.files)}
                </td>
                <td>
                  {item.editable ? (
                    <div className="select-box c-max-x-small">
                      <select
                        className="status-select"
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      >
                        <option value="承認">承認</option>
                        <option value="却下">却下</option>
                      </select>
                    </div>
                  ) : (
                    <span className={`status-tag status-${item.status}`}>{item.status}</span>
                  )}
                </td>
              </tr>
              {/* コメント欄を表示（却下かつ編集可能な場合のみ） */}
              {showCommentFor[item.id] && (
                <tr className="comment-row">
                  <td></td>
                  <td className="space-one-column" colSpan={5}>
                    <div className="comment-label">コメント</div>
                    <div className="comment-box">
                      <textarea
                        value={comments[item.id] || ''}
                        onChange={(e) => handleCommentChange(item.id, e.target.value)}
                        className="comment-input"
                      />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    });
  };

  // ページネーションコンポーネント
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    // 現在のページを中心に最大5ページ分のページ番号を表示
    const displayPages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    for (let i = startPage; i <= endPage; i++) {
      displayPages.push(i);
    }

    return (
      <div className="pagination">
        <span
          className={`pagination-prev ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        >
          ＜
        </span>

        {startPage > 1 && (
          <>
            <span
              className={`pagination-page ${1 === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(1)}
            >
              1
            </span>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}

        {displayPages.map(page => (
          <span
            key={page}
            className={`pagination-page ${page === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </span>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <span
              className={`pagination-page ${totalPages === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </span>
          </>
        )}

        <span
          className={`pagination-next ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
        >
          ＞
        </span>
      </div>
    );
  };

  return (
    <div className="insurance-application-list-page">
      <AdminHeader onMenuToggle={(isOpen) => {
        // メニューの状態が変更された時の処理をここに追加できます
      }} />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>登録保険会社申請一覧</h1>
            </div>
          </div>

          <div className="application-list-container">
            <div className="search-filters">
              <div className="status-filters">
                <span className="status-label">状態</span>
                <div className="status-buttons">
                  <button
                    className={`status-button ${statusFilter === 'すべて' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('すべて')}
                  >
                    すべて
                  </button>
                  <button
                    className={`status-button ${statusFilter === '未処理' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('未処理')}
                  >
                    未処理
                  </button>
                  <button
                    className={`status-button ${statusFilter === '却下' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('却下')}
                  >
                    却下
                  </button>
                  <button
                    className={`status-button ${statusFilter === '承認' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('承認')}
                  >
                    承認
                  </button>
                </div>
              </div>

              <div className="agency-filter">
                <span className="agency-label">代理店</span>
                <div className="agency-box c-max-l-mini">
                  <select
                    className="agency-select c-max-l-mini"
                    value={agencyFilter}
                    onChange={handleAgencyFilterChange}
                  >
                    <option value="すべて">すべての代理店</option>
                    {agencies.map((agency, index) => (
                      <option key={index} value={agency.name}>{agency.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="status-filters">
                <span className="status-label">生保/損保</span>
                <div className="status-buttons">
                  <button
                    className={`status-button ${typeFilter === INSURANCE_TYPE.ALL ? 'active' : ''}`}
                    onClick={() => handleTypeFilterChange(INSURANCE_TYPE.ALL)}
                  >
                    すべて
                  </button>
                  <button
                    className={`status-button ${typeFilter === INSURANCE_TYPE.LIFE ? 'active' : ''}`}
                    onClick={() => handleTypeFilterChange(INSURANCE_TYPE.LIFE)}
                  >
                    {getInsuranceTypeShortName(INSURANCE_TYPE.LIFE)}
                  </button>
                  <button
                    className={`status-button ${typeFilter === INSURANCE_TYPE.SONPO ? 'active' : ''}`}
                    onClick={() => handleTypeFilterChange(INSURANCE_TYPE.SONPO)}
                  >
                    {getInsuranceTypeShortName(INSURANCE_TYPE.SONPO)}
                  </button>
                </div>
              </div>
            </div>
            {renderPagination()}

            {loading ? (
              <div className="loading-indicator">データを読み込み中...</div>
            ) : (
              <table className="applications-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      &nbsp;
                    </th>
                    <th className="date-column">申請日</th>
                    <th className="agency-column">申請代理店</th>
                    <th className="insurance-type">生保/損保</th>
                    <th className="insurance-column">申請保険会社</th>
                    <th className="type-column">申請書類</th>
                    <th className="status-column">状態</th>
                  </tr>
                </thead>
                <tbody>
                  {/* グループ化されたデータを表示 */}
                  {renderGroupedApplications()}

                  {/* 検索結果が0件の場合 */}
                  {filteredApplications.length === 0 && (
                    <tr>
                      <td colSpan={6} className="no-results">
                        条件に一致する申請はありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            <div className="action-buttons c-max-mini c-m-center">
              <button className="button button-secondary button-medium" onClick={handleReturn}>戻る</button>
              <button
                className="btn btn-primary button-medium"
                onClick={handleConfirm}
                disabled={!selectedApplication}
              >
                確認する
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PDFポップアップコンポーネント */}
      <InsuranceApplicationPDFPop
        showPopup={showPopup}
        closePopup={closePopup}
        documentIds={popupFiles.map(file => file.id).filter(Boolean)}
        currentDocumentIndex={currentFileIndex}
        setCurrentDocumentIndex={setCurrentFileIndex}
      />

      {/* 確認ポップアップコンポーネント */}
      <InsuranceApplicationListPop
        showConfirmationPopup={showConfirmationPopup}
        confirmationPopupData={confirmationPopupData}
        comments={comments}
        closeConfirmationPopup={closeConfirmationPopup}
        finalizeStatusChange={finalizeStatusChange}
      />
    </div>
  );
};

export default InsuranceApplicationList;